import { Text, View, ScrollView, TouchableOpacity, Linking, Platform } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useCallback, useMemo } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import { Query } from '@/utils/react-query/Queries';
import Unicon from '@/components/General/Unicon';
import ProfileCard from '@/components/People/ProfileCard';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import ModalWrapper from '@/components/General/ModalWrapper';

export default function AddressDetailModal() {
    const { addressID } = useLocalSearchParams();
    const router = useRouter();

    const { data: address, isLoading } = Query.addresses.byId({ id: addressID, options: { enabled: !!addressID } });
    const { data: loggedInUser } = Query.people.loggedInUser();

    const fullAddress = useMemo(() => {
        if (!address) return null;
        const parts = [
            address.street,
            address.streetLine2,
            address.city,
            address.state && address.zip ? `${address.state} ${address.zip}` : (address.state || address.zip),
        ].filter(Boolean);
        return parts.join(', ');
    }, [address]);

    const activeMembers = useMemo(() => {
        return (address?.HouseholdMembers || []).filter(p => !p.isArchived);
    }, [address?.HouseholdMembers]);

    const archivedMembers = useMemo(() => {
        return (address?.HouseholdMembers || []).filter(p => p.isArchived);
    }, [address?.HouseholdMembers]);

    const openMaps = useCallback(() => {
        if (!fullAddress) return;
        const encoded = encodeURIComponent(fullAddress);
        const url = Platform.OS === 'ios'
            ? `maps://?q=${encoded}`
            : `https://maps.google.com/?q=${encoded}`;
        Linking.openURL(url);
    }, [fullAddress]);

    const isOwnAddress = loggedInUser?.addressID === addressID;
    const canEdit = loggedInUser?.can?.('Edit People') || (isOwnAddress);

    if (isLoading) {
        return (
            <ModalWrapper title="Address Details">
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" />
                </View>
            </ModalWrapper>
        );
    }

    if (!address) {
        return (
            <ModalWrapper title="Address Details">
                <EmptyListPlaceholder type="404" message="Address not found" />
            </ModalWrapper>
        );
    }

    return (
        <ModalWrapper
            title="Address Details"
            cancelText="Close"
            noPadding
        >
            <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}keyboardShouldPersistTaps="handled"
>
                {/* Hero Card */}
                <View style={styles.heroCard}>
                    <View style={styles.heroIconBadge}>
                        <Unicon name="home" size={28} colorVariant="primary" />
                    </View>
                    <View style={styles.heroInfo}>
                        {address.street ? (
                            <Text style={styles.heroStreet}>{address.street}</Text>
                        ) : null}
                        {address.streetLine2 ? (
                            <Text style={styles.heroLine2}>{address.streetLine2}</Text>
                        ) : null}
                        <Text style={styles.heroCityState}>
                            {[address.city, address.state, address.zip].filter(Boolean).join(', ')}
                        </Text>
                    </View>
                    {canEdit ? (
                        <TouchableOpacity
                            style={styles.heroEditBtn}
                            onPress={() => router.push({ pathname: '/addresses/create', params: { id: addressID } })}
                        >
                            <Unicon name="create-outline" size={18} colorVariant="primary" />
                        </TouchableOpacity>
                    ) : null}
                </View>

                {/* Map Actions */}
                {fullAddress ? (
                    <View style={styles.mapActionsRow}>
                        <TouchableOpacity style={styles.mapBtn} onPress={openMaps}>
                            <Unicon name="map-outline" size={16} colorVariant="primary" />
                            <Text style={styles.mapBtnText}>Open in Maps</Text>
                        </TouchableOpacity>
                    </View>
                ) : null}

                {/* People Section */}
                <View style={styles.section}>
                    <View style={styles.sectionHeader}>
                        <Text style={styles.sectionLabel}>People at this Address</Text>
                        <View style={styles.sectionBadge}>
                            <Text style={styles.sectionBadgeText}>{activeMembers.length}</Text>
                        </View>
                    </View>

                    {activeMembers.length > 0 ? (
                        <View style={styles.card}>
                            {activeMembers.map((person, idx) => (
                                <View key={person.id} style={[styles.personRow, idx < activeMembers.length - 1 && styles.personRowBorder]}>
                                    <ProfileCard
                                        profile={person}
                                        minimal
                                        onPress={() => router.push('/people/' + person.id)}
                                    />
                                </View>
                            ))}
                        </View>
                    ) : (
                        <View style={styles.emptyCard}>
                            <Unicon name="people-outline" size={24} colorVariant="textSecondary" />
                            <Text style={styles.emptyCardText}>No people linked to this address.</Text>
                        </View>
                    )}

                    {archivedMembers.length > 0 ? (
                        <View style={[styles.card, styles.archivedCard]}>
                            <Text style={styles.archivedLabel}>Archived</Text>
                            {archivedMembers.map((person, idx) => (
                                <View key={person.id} style={[styles.personRow, idx < archivedMembers.length - 1 && styles.personRowBorder]}>
                                    <ProfileCard
                                        profile={person}
                                        minimal
                                        onPress={() => router.push('/people/' + person.id)}
                                    />
                                </View>
                            ))}
                        </View>
                    ) : null}
                </View>

                {/* Locations Section */}
                <View style={styles.section}>
                    <View style={styles.sectionHeader}>
                        <Text style={styles.sectionLabel}>Locations at this Address</Text>
                        <View style={styles.sectionBadge}>
                            <Text style={styles.sectionBadgeText}>{(address?.Locations || []).length}</Text>
                        </View>
                    </View>

                    {address?.Locations?.length > 0 ? (
                        <View style={styles.card}>
                            {address.Locations.map((loc, idx) => (
                                <TouchableOpacity
                                    key={loc.id}
                                    style={[styles.locationRow, idx < address.Locations.length - 1 && styles.locationRowBorder]}
                                    onPress={() => loc?.id && router.push('/locations/' + loc.id)}
                                >
                                    <View style={styles.locationIconBadge}>
                                        <Unicon name="location" size={16} colorVariant="secondary" />
                                    </View>
                                    <View style={styles.locationInfo}>
                                        <Text style={styles.locationName}>{loc.name}</Text>
                                        {loc.detailedArea ? (
                                            <Text style={styles.locationDetail}>{loc.detailedArea}</Text>
                                        ) : null}
                                    </View>
                                    <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
                                </TouchableOpacity>
                            ))}
                        </View>
                    ) : (
                        <View style={styles.emptyCard}>
                            <Unicon name="location-outline" size={24} colorVariant="textSecondary" />
                            <Text style={styles.emptyCardText}>No locations linked to this address.</Text>
                        </View>
                    )}
                </View>

            </ScrollView>
        </ModalWrapper>
    );
}

const styles = StyleSheet.create((theme) => ({
    loadingContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },

    // Content
    content: {
        padding: 16,
        paddingBottom: 60,
    },

    // Hero Card
    heroCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        borderRadius: 14,
        padding: 20,
        marginBottom: 16,
        boxShadow: theme.shadows.card,
        gap: 16,
    },
    heroEditBtn: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: theme.colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    heroIconBadge: {
        width: 60,
        height: 60,
        borderRadius: 14,
        backgroundColor: theme.colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    heroInfo: {
        flex: 1,
        gap: 2,
    },
    heroStreet: {
        fontSize: 18,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
    heroLine2: {
        fontSize: 14,
        color: theme.colors.textSecondary,
    },
    heroCityState: {
        fontSize: 14,
        color: theme.colors.textSecondary,
    },

    // Map Actions
    mapActionsRow: {
        flexDirection: 'row',
        marginBottom: 20,
        gap: 10,
    },
    mapBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingVertical: 10,
        paddingHorizontal: 16,
        borderRadius: 10,
        backgroundColor: theme.colors.primaryContainer,
    },
    mapBtnText: {
        fontSize: 14,
        color: theme.colors.primary,
        fontFamily: theme.fonts.bold,
    },

    // Section
    section: {
        marginBottom: 20,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 10,
        marginLeft: 4,
        gap: 8,
    },
    sectionLabel: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
    },
    sectionBadge: {
        backgroundColor: theme.colors.surfaceVariant,
        borderRadius: 10,
        paddingHorizontal: 8,
        paddingVertical: 2,
    },
    sectionBadgeText: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
    },

    // Card
    card: {
        backgroundColor: theme.colors.surface,
        borderRadius: 12,
        boxShadow: theme.shadows.card,
        overflow: 'hidden',
    },
    archivedCard: {
        marginTop: 10,
        opacity: 0.65,
    },
    archivedLabel: {
        fontSize: 10,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        paddingHorizontal: 14,
        paddingTop: 10,
        paddingBottom: 4,
    },
    emptyCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 12,
        boxShadow: theme.shadows.card,
        padding: 20,
        alignItems: 'center',
        gap: 8,
    },
    emptyCardText: {
        fontSize: 14,
        color: theme.colors.textSecondary,
        fontStyle: 'italic',
        textAlign: 'center',
    },

    // Person Row
    personRow: {
        paddingHorizontal: 2,
    },
    personRowBorder: {
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
    },

    // Location Row
    locationRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        padding: 14,
    },
    locationRowBorder: {
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
    },
    locationIconBadge: {
        width: 36,
        height: 36,
        borderRadius: 8,
        backgroundColor: theme.colors.secondaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    locationInfo: {
        flex: 1,
        gap: 2,
    },
    locationName: {
        fontSize: 14,
        fontFamily: theme.fonts.bold,
        color: theme.colors.text,
    },
    locationDetail: {
        fontSize: 12,
        color: theme.colors.textSecondary,
    },
}));
